import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { truncateNumber } from '../../../utils/commonFunctions';
import useGenericSimulator from './useGenericSimulator';
import useUrlParams from './useUrlParams';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Interval (ms) at which the simulation timer UI updates (progress bar & time). */
const TICK_INTERVAL_MS = 1000;

/** Relative start time for simulation (always 0 – we use relative seconds). */
const SCENARIO_RELATIVE_START = 0;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScenarioTimeValues {
    /** Relative start time in seconds (0). */
    scenarioStartTime: number;
    /** Relative end time in seconds (scenarioDuration * 60). */
    scenarioEndTime: number;
    /** Absolute Unix start time in seconds (from scenario.scenarioStartTime). */
    scenarioStartTimeAbs: number;
    /** Current relative time in seconds from scenario payload. */
    fromScenarioRel: number;
}

export interface UseSimulationTimerResult {
    /** Current simulation time in seconds (ticks every second). */
    currentSimulationTimeSec: number;
    /** Current simulation time in milliseconds. */
    currentSimulationTimeMs: number;
    /** Relative start time (0). */
    scenarioStartTime: number;
    /** Relative end time in seconds. */
    scenarioEndTime: number;
    /** Absolute Unix start time in seconds. */
    scenarioStartTimeAbs: number;
    /** Total simulation duration in minutes (for display). */
    simulationDurationMins: number;
    /** Whether to show the scenario timer block (progress bar + time). */
    showScenarioTimerContainer: boolean;
    /** Whether to show the "X mins / Y mins" summary-style duration. */
    showSummaryTimerContainer: boolean;
    /** Returns elapsed duration in minutes (for "X mins" display). */
    getDurationMins: () => number;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Derives scenario time values from the scenario payload (deploymentData.data).
 * Uses relative time for progress (0 to scenarioDuration*60 sec) and
 * absolute Unix time for formatted timestamps.
 */
export function getScenarioTimeFromScenario(scenario: any): ScenarioTimeValues {
    const simulationDurationMins = scenario?.scenarioDuration ?? 0;
    const scenarioEndTime = simulationDurationMins * 60; // convert minutes to seconds
    const scenarioStartTimeAbs = scenario?.scenarioStartTime ?? 0;
    const fromScenarioRel = scenario?.elements?.[0]?.simulationTimeRel ?? 0;

    return {
        scenarioStartTime: SCENARIO_RELATIVE_START,
        scenarioEndTime,
        scenarioStartTimeAbs,
        fromScenarioRel,
    };
}

/**
 * Computes total simulation duration in minutes (for "Y mins" display).
 */
export function getSimulationDurationMins(scenarioEndTime: number): number {
    return truncateNumber((scenarioEndTime - SCENARIO_RELATIVE_START) / 60, 0);
}

/**
 * Computes elapsed duration in minutes from current simulation time (ms).
 */
export function getDurationMinsFromMs(
    currentSimulationTimeMs: number,
    scenarioStartTime: number
): number {
    return truncateNumber(
        (currentSimulationTimeMs - scenarioStartTime * 1000) / 60000,
        0
    );
}

// ---------------------------------------------------------------------------
// Hook: useSimulationTimer
// ---------------------------------------------------------------------------

/**
 * Encapsulates simulation timer state and ticking logic for the footer.
 *
 * Live mode:
 *   - Syncs with scenario payload: when elements[0].simulationTimeRel or
 *     scenarioDuration changes, the displayed time is updated.
 *   - Runs a 1s interval while the scenario is running so the progress bar
 *     and time display "tick" every second without waiting for backend updates.
 *
 * Summary mode:
 *   - Uses replayState (currentTimeMs / endTimeMs) from the summary-replay system.
 */
export function useSimulationTimer(): UseSimulationTimerResult {
    const { deploymentData, replayState } = useGenericSimulator();
    const { dashboard } = useUrlParams();
    const isSummary = dashboard === 'summary';

    const scenario = deploymentData?.data;

    const [tickingCurrentSec, setTickingCurrentSec] = useState(0);
    const baseRef = useRef({ relSec: 0, ts: 0 });

    const {
        scenarioStartTime,
        scenarioEndTime,
        scenarioStartTimeAbs,
        fromScenarioRel,
    } = useMemo(
        () => getScenarioTimeFromScenario(scenario),
        [
            scenario?.scenarioDuration,
            scenario?.scenarioStartTime,
            scenario?.elements?.[0]?.simulationTimeRel,
        ]
    );

    // ---------- Live mode ----------

    // Sync displayed time when backend sends new simulationTimeRel or duration changes
    useEffect(() => {
        if (isSummary) return;
        if (typeof fromScenarioRel === 'number' && scenarioEndTime > 0) {
            baseRef.current = { relSec: fromScenarioRel, ts: Date.now() };
            setTickingCurrentSec(fromScenarioRel);
        }
    }, [fromScenarioRel, scenarioEndTime, isSummary]);

    // Tick every second so progress bar and time update smoothly
    useEffect(() => {
        if (isSummary) return;
        if (scenario?.status !== 'SimulationRunning' || scenarioEndTime <= 0) return;
        const id = setInterval(() => {
            const { relSec, ts } = baseRef.current;
            const elapsed = Math.floor((Date.now() - ts) / 1000);
            const next = Math.min(relSec + elapsed, scenarioEndTime);
            setTickingCurrentSec(next);
        }, TICK_INTERVAL_MS);
        return () => clearInterval(id);
    }, [scenario?.status, scenarioEndTime, isSummary]);

    // ---------- Summary mode ----------

    // In summary mode derive end time from replayState if deployment data lacks it
    const summaryEndTimeSec = useMemo(() => {
        if (scenarioEndTime > 0) return scenarioEndTime;
        if (replayState.endTimeMs > 0) return replayState.endTimeMs / 1000;
        return 0;
    }, [scenarioEndTime, replayState.endTimeMs]);

    // ---------- Final values ----------

    const currentSimulationTimeSec = isSummary
        ? (replayState.currentTimeMs ?? 0) / 1000
        : tickingCurrentSec;

    const effectiveEndTime = isSummary ? summaryEndTimeSec : scenarioEndTime;

    const currentSimulationTimeMs = currentSimulationTimeSec * 1000;
    const simulationDurationMins = useMemo(
        () => getSimulationDurationMins(effectiveEndTime),
        [effectiveEndTime]
    );

    const showScenarioTimerContainer = isSummary
        ? effectiveEndTime > 0
        : scenario?.status === 'SimulationRunning' && scenarioEndTime > 0;

    const showSummaryTimerContainer =
        isSummary && currentSimulationTimeSec >= 0;

    const getDurationMins = useCallback(() =>
        getDurationMinsFromMs(currentSimulationTimeMs, scenarioStartTime),
    [currentSimulationTimeMs, scenarioStartTime]);

    return {
        currentSimulationTimeSec,
        currentSimulationTimeMs,
        scenarioStartTime,
        scenarioEndTime: effectiveEndTime,
        scenarioStartTimeAbs,
        simulationDurationMins,
        showScenarioTimerContainer,
        showSummaryTimerContainer,
        getDurationMins,
    };
}

export default useSimulationTimer;
